import { useState, useCallback, useEffect, createContext } from 'react';
import { useUrlPersistentPostcode } from './useUrlPersistentPostcode';
import usePlatformConfig from './usePlatformConfig';
import { DEFAULT_DEALER_POSTCODE } from '../dealerList/dealersConstants';
import useSalesEnablementCommunication from '../hooks/useSalesEnablementCommunication';
import useCustomerStorage from '../hooks/useCustomerStorage';

interface IModalState {
  isModalOpen: boolean;
  displayCloseIcon: boolean;
}

interface IRefreshState {
  needsRefresh: boolean;
}

export type IPostCodeContext = IRefreshState &
  IModalState & {
    openModal: (shouldOpen: boolean, displayCloseIcon?: boolean) => void;
    enableRefresh: () => void;
    disableRefresh: () => void;
  };

export const PostCodeContext = createContext<IPostCodeContext>({
  isModalOpen: true,
  displayCloseIcon: false,
  openModal: () => null,
  enableRefresh: () => null,
  disableRefresh: () => null,
  needsRefresh: false,
});

export const getPostCodeModalContext: () => IPostCodeContext = () => {
  const [modalState, openModalState] = useState<IModalState>({
    isModalOpen: false,
    displayCloseIcon: false,
  });

  const [refreshState, setRefreshState] = useState<IRefreshState>({
    needsRefresh: false,
  });

  const {
    platformConfig: { isDealerSelectionEnabled },
  } = usePlatformConfig();
  const { customerStorage } = useCustomerStorage();
  const storagePostcode = customerStorage.getCustomerData()?.postcode;
  const sessionStoragePostcode = customerStorage.getCustomerDataSession()?.postcode;
  const { getUrlPostcode } = useUrlPersistentPostcode();
  const urlPersitentPostcode = getUrlPostcode()?.param;
  const { isInSalesEnablement } = useSalesEnablementCommunication();

  const enableRefresh = useCallback(() => {
    setRefreshState({
      needsRefresh: true,
    });
  }, []);

  const disableRefresh = useCallback(() => {
    setRefreshState({
      ...refreshState,
      needsRefresh: false,
    });
  }, []);

  const openModal = useCallback(
    (shouldOpen: boolean, displayCloseIcon?: boolean) => {
      openModalState({
        isModalOpen: shouldOpen,
        displayCloseIcon: !!displayCloseIcon,
      });
    },
    [modalState],
  );

  // Check for existing dealer information
  useEffect(() => {
    if (isInSalesEnablement && !sessionStoragePostcode) {
      openModal(true);
    } else if (!isDealerSelectionEnabled) {
      // Set default postcode if dealer selection is disabled.
      const storageInfo = customerStorage.getCustomerData();

      storageInfo.postcode = DEFAULT_DEALER_POSTCODE;

      customerStorage.setCustomerData(storageInfo);
    } else if (isDealerSelectionEnabled && !storagePostcode && !urlPersitentPostcode) {
      openModal(true);
    }
  }, []);

  return { ...modalState, openModal, ...refreshState, enableRefresh, disableRefresh };
};
